import React, { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Controller, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';
import cloneDeep from 'lodash/cloneDeep';

import { CloseIcon } from '@graphiql/react';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import { workspaceBlocksMockByTemplateNameMock } from '../mocks/htmlBlocks';
import { getAxiosApiV2WithAuthorization, useDeepCompareEffect, useDeepCompareMemo } from '@clatter/platform';
import {
  Block,
  mapBlockToStore,
  useGetProjectTemplateQueriesQuery,
  useUpdateDocumentTemplateMutation,
  useUpdateMicrositeTemplateBlockMutation,
} from '../../../../../../../../services';
import { config } from '../../../../../../../../config';
import { addNotification } from '../../../../../../../../store';
import { listActionTypes } from '../../../../../../../../constants';

import { NgDivider, NgErrorMessage } from '../../../../../../../UI';
import { FolderManagerDynamicForm } from '../../../../../../../FolderManager';
import { handlebarsToJsonSchemaV2 } from './handlebarsToJsonSchema';
import { HtmlTemplatePreview } from './components/HtmlTemplatePreview';
import { BlockInputsSettings } from './components/BlockInputsSettings';
import { useGetBlocksQuery, useGetLayoutsQuery } from 'apps/ng/src/app/services/blocks.service';
import { handlebarsToPartialsWIthIds } from './handlebarsToPartialsWIthIds';
import { ProjectTemplateWorkbookUploadEventBus } from 'apps/ng/src/app/events/project-template-workbook-upload';
import { BlockPreview } from '../../../../../Blocks/components/BlockPreview';

const StyledPageTemplateEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};

  label {
    display: inline-block;
    font-size: ${({ theme }) => theme.fontSize.default};
    margin-bottom: ${({ theme }) => theme.spacing.small};
  }

  .blocks-list-container {
    max-height: 618px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;

    li {
      padding: ${({ theme }) => theme.spacing.small};
      border: 1px solid lightgray;
      margin-bottom: ${({ theme }) => theme.spacing.small};
      border-radius: 4px;
      display: flex;
      flex-direction: column;

      &.disabled {
        pointer-events: none;
        opacity: 0.4;
      }

      button {
        width: 25px;
        height: 25px;

        svg {
          font-size: 0.3rem;
        }
      }
    }
  }
`;

const StyledIconButton = styled.span`
  cursor: pointer;
`;

const StyledThumbnailWrapper = styled.div`
  width: 100%;
  height: 150px;
  overflow: hidden;
  position: relative;
`;

const StyledThumbnail = styled.div`
  transform: scale(0.25);
  transform-origin: top left;
  width: 400%;
  min-height: 600px;

  /* Hide the preview header */
  > div > div:first-child {
    display: none;
  }

  /* Force iframe to render at desktop width before scaling */
  iframe {
    min-height: 600px;
    min-width: 1200px;
  }
`;

const StyledNoPreview = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #999;
  font-size: 13px;
`;

// Component to render block thumbnail preview
const BlockThumbnailPreview: React.FC<{ block: Block; allBlocks: Block[] }> = ({ block, allBlocks }) => {
  // Check if block has variables defined
  const hasVariables = block.variables && Object.keys(block.variables).length > 0;

  if (!hasVariables) {
    return <StyledNoPreview>No Preview</StyledNoPreview>;
  }

  return (
    <StyledThumbnailWrapper>
      <StyledThumbnail>
        <BlockPreview
          blocks={allBlocks}
          htmlCode={block.html || ''}
          cssCode={block.blockStylesheet?.css || ''}
          jsCode={block.blockScript?.js || ''}
          variables={JSON.stringify(block.variables || {})}
        />
      </StyledThumbnail>
    </StyledThumbnailWrapper>
  );
};

const updateLayoutByAction = ({
  items,
  block,
  action,
}: {
  items: HtmlTemplateBlock[];
  block: HtmlTemplateBlock;
  action: string;
}): HtmlTemplateBlock[] => {
  const index = items.findIndex((item) => item.instanceId === block.instanceId);

  if (index === -1) {
    console.warn('Block item not found!', block);
    return items;
  }
  const newItems = [...items];

  switch (action) {
    case listActionTypes.MOVE_UP:
      if (index === 0) return items;
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;

    case listActionTypes.MOVE_DOWN:
      if (index === items.length - 1) return items;
      [newItems[index + 1], newItems[index]] = [newItems[index], newItems[index + 1]];
      return newItems;

    case listActionTypes.REMOVE:
      newItems.splice(index, 1);
      return newItems;

    default:
      return items;
  }
};

const formSchema = {
  type: 'object',
  properties: {
    display_name: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'This field is required!',
      },
    },
    layout: {
      type: 'object',
      properties: {
        label: { type: 'string' },
        value: { type: 'string' },
      },
    },
    pageBlocks: {
      type: 'array',
    },
  },
  required: ['display_name', 'layout'],
};

const encodePageName = (str: string) => {
  return str
    .trim()
    .toLowerCase()
    .split(/[\s_-]+/)
    .join('-');
};

// Helper to generate unique block keys for schema and context
const getBlockKey = (blockName: string, instanceId?: string) => {
  // For backwards compatibility: if no instanceId, use just blockName
  return instanceId ? `${blockName}_${instanceId}` : blockName;
};
// Helper to serialize pageBlocks with columns for storage
const serializePageBlocksForStorage = (pageBlocks: any[]): any[] => {
  if (!pageBlocks || !Array.isArray(pageBlocks)) {
    return [];
  }
  return pageBlocks.map((block) => {
    const serialized: any = {
      id: block.id,
      name: block.name,
      type: block.type,
      instanceId: block.instanceId,
      displayName: block.displayName,
    };

    // Include columns if they exist
    if (block.columns && Object.keys(block.columns).length > 0) {
      serialized.columns = {};
      for (const [columnName, columnBlocks] of Object.entries(block.columns)) {
        serialized.columns[columnName] = (columnBlocks as any[]).map((cb) => ({
          id: cb.id,
          name: cb.name,
          type: cb.type,
          instanceId: cb.instanceId,
          displayName: cb.displayName,
        }));
      }
    }

    return serialized;
  });
};

// Helper to collect all blocks including those in columns for allBlocksUsed
const collectAllBlocksFromPageBlocks = (pageBlocks: any[]): any[] => {
  const allBlocks: any[] = [];

  if (!pageBlocks || !Array.isArray(pageBlocks)) {
    return allBlocks;
  }

  for (const block of pageBlocks) {
    // Add the block itself
    allBlocks.push({ id: block.id, name: block.name, type: block.type });

    // Add blocks from columns if they exist
    if (block.columns) {
      for (const [columnName, columnBlocks] of Object.entries(block.columns)) {
        (columnBlocks as any[]).forEach((cb) => {
          allBlocks.push({ id: cb.id, name: cb.name, type: cb.type });
        });
      }
    }
  }

  return allBlocks;
};

// Helper to collect all block keys including those in columns for schema cleanup
const collectAllBlockKeysFromPageBlocks = (pageBlocks: any[]): Set<string> => {
  const allBlockKeys = new Set<string>();

  if (!pageBlocks || !Array.isArray(pageBlocks)) {
    return allBlockKeys;
  }

  for (const block of pageBlocks) {
    // Add the block itself
    allBlockKeys.add(getBlockKey(block.name, block.instanceId));

    // Add blocks from columns if they exist
    if (block.columns) {
      for (const [columnName, columnBlocks] of Object.entries(block.columns)) {
        (columnBlocks as any[]).forEach((cb) => {
          allBlockKeys.add(getBlockKey(cb.name, cb.instanceId));
        });
      }
    }
  }

  return allBlockKeys;
};

const generateHtmlTemplate = ({
  layoutData,
  pages,
  pageBlocks,
  pageTemplateName,
  allBlocks,
  pageBlock,
  sections,
  valuesSchema: valuesSchemaBefore,
}: any) => {
  if (!layoutData || !pageTemplateName || !pageBlock) {
    return {
      type: 'error',
      message: 'Missing required data to generate template!',
    };
  }

  const isCothromLayout = layoutData.name.includes('cothrom');
  const pageKey = encodePageName(pageTemplateName);
  const pageKeyBefore = encodePageName(pageBlock.displayName);

  // build initial pageBlock HTML
  let pageBlockHtml = isCothromLayout ? `{{#> ${layoutData.name} }} ` : `{{#> ${layoutData.name} [${pageKey}]}}`;

  for (const block of pageBlocks || []) {
    const blockKey = getBlockKey(block.name, block.instanceId);
    pageBlockHtml += isCothromLayout ? `{{> ${block.name} }}` : `{{> ${block.name} [${blockKey}] }}`;
  }

  pageBlockHtml += ` {{/ ${layoutData.name} }}`;

  // map all blocks for partials
  const allBlocksMapped = [layoutData, ...(allBlocks || [])].map((b: any) => ({
    id: b.id,
    name: b.name,
    partial: b.html || '',
    type: b.type,
  }));

  // serialize page blocks (including columns)
  const serializedPageBlocks = serializePageBlocksForStorage(pageBlocks);

  // update template pages
  const updatedHtmlTemplatePages = (pages || []).map((page: any) => {
    if (page.id === pageBlock.id) {
      // collect blocks used in HTML (including columns)
      const blocksInHtml = handlebarsToPartialsWIthIds(pageBlockHtml, allBlocksMapped);
      const allBlocksUsedSet = new Set(blocksInHtml.map((b: any) => b.id));

      serializedPageBlocks.forEach((block: any) => {
        if (block.columns) {
          Object.values(block.columns).forEach((columnBlocks: any) => {
            columnBlocks.forEach((cb: any) => {
              if (!allBlocksUsedSet.has(cb.id)) {
                const fullBlockData = allBlocksMapped.find((b: any) => b.id === cb.id);
                if (fullBlockData) {
                  blocksInHtml.push(fullBlockData);
                  allBlocksUsedSet.add(cb.id);
                }
              }
            });
          });
        }
      });

      // Strip HTML/partial from allBlocksUsed - only keep references
      const cleanedBlocksUsed = blocksInHtml.map((b: any) => ({
        id: b.id,
        name: b.name,
        type: b.type,
      }));

      return {
        ...page,
        name: pageKey,
        displayName: pageTemplateName,
        blocks: [
          {
            id: layoutData.id,
            name: layoutData.name,
            type: layoutData.type,
            displayName: layoutData.displayName,
          },
          ...serializedPageBlocks,
        ],
        allBlocksUsed: cleanedBlocksUsed,
        sections,
      };
    }
    return page;
  });

  //existing schema for page (use old key if page was renamed)
  const existingPageSchema = valuesSchemaBefore?.properties?.[pageKeyBefore]?.properties || {};

  // Build new schema with updated page key
  const newProperties = { ...valuesSchemaBefore?.properties };

  // Remove old key if page was renamed
  if (pageKey !== pageKeyBefore && newProperties[pageKeyBefore]) {
    delete newProperties[pageKeyBefore];
  }

  const valuesSchema: Record<string, any> = {
    type: 'object',
    properties: {
      ...newProperties,
      [pageKey]: {
        type: 'object',
        properties: {
          ...existingPageSchema,
        },
      },
    },
  };

  // add layout schema if present
  if (layoutData?.schema && Object.keys(layoutData.schema).length) {
    const clonedLayoutSchema = cloneDeep(layoutData.schema);
    const updatedProperties = { ...clonedLayoutSchema.properties };
    for (const key of Object.keys(updatedProperties)) {
      updatedProperties[key] = {
        ...updatedProperties[key],
        inputOptions: {
          ...updatedProperties[key]?.inputOptions,
          gqlQueryKey: `${pageKey}_${key}`,
        },
      };
    }
    //@ts-ignore
    valuesSchema.properties[pageKey].properties[layoutData.name] = {
      ...clonedLayoutSchema,
      properties: updatedProperties,
    };
  }

  // process all blocks to set inputOrder and generate schema for new blocks
  // track current order index across main blocks and column blocks
  let currentOrder = 0;

  for (let i = 0; i < (pageBlocks || []).length; i++) {
    const block = pageBlocks[i];
    const blockKey = getBlockKey(block.name, block.instanceId);

    // if schema already exists, just update inputOrder
    if (existingPageSchema[blockKey]) {
      //@ts-ignore
      valuesSchema.properties[pageKey].properties[blockKey] = {
        ...existingPageSchema[blockKey],
        inputOrder: currentOrder,
      };
    } else {
      // generate schema for new blocks
      const newBlockSchema = handlebarsToJsonSchemaV2(block.name, [
        { name: layoutData.name, partial: layoutData.html || '' },
        { name: block.name, partial: block.html || '' },
        ...allBlocksMapped,
      ]);

      // only add schema if block has input fields
      if (newBlockSchema && newBlockSchema.properties && Object.keys(newBlockSchema.properties).length > 0) {
        //@ts-ignore
        valuesSchema.properties[pageKey].properties[blockKey] = {
          ...newBlockSchema,
          inputType: 'object',
          //@ts-ignore
          title: block.displayName || newBlockSchema.title || block.name,
          inputOrder: currentOrder,
          required: [],
          inputOptions: {
            ...(newBlockSchema.inputOptions || {}),
            gqlQueryKey: `${pageKey}_${blockKey}`,
            blockInstanceId: block.instanceId,
            originalBlockName: block.name,
          },
        };
      }
    }

    currentOrder++;

    // Process blocks inside columns (if any)
    if (block.columns) {
      for (const [columnName, columnBlocks] of Object.entries(block.columns)) {
        for (const columnBlock of columnBlocks as any[]) {
          const columnBlockKey = getBlockKey(columnBlock.name, columnBlock.instanceId);

          // If schema already exists, just update inputOrder
          if (existingPageSchema[columnBlockKey]) {
            //@ts-ignore
            valuesSchema.properties[pageKey].properties[columnBlockKey] = {
              ...existingPageSchema[columnBlockKey],
              inputOrder: currentOrder,
            };
          } else {
            // generate schema for new column blocks
            const columnBlockSchema = handlebarsToJsonSchemaV2(columnBlock.name, [
              { name: layoutData.name, partial: layoutData.html || '' },
              { name: columnBlock.name, partial: columnBlock.html || '' },
              ...allBlocksMapped,
            ]);

            // only add schema if block has input fields
            if (
              columnBlockSchema &&
              columnBlockSchema.properties &&
              Object.keys(columnBlockSchema.properties).length > 0
            ) {
              //@ts-ignore
              valuesSchema.properties[pageKey].properties[columnBlockKey] = {
                ...columnBlockSchema,
                inputType: 'object',
                //@ts-ignore
                title: columnBlock.displayName || columnBlockSchema.title || columnBlock.name,
                inputOrder: currentOrder,
                required: [],
                inputOptions: {
                  ...(columnBlockSchema.inputOptions || {}),
                  gqlQueryKey: `${pageKey}_${columnBlockKey}`,
                  blockInstanceId: columnBlock.instanceId,
                  originalBlockName: columnBlock.name,
                },
              };
            }
          }

          currentOrder++;
        }
      }
    }
  }

  // clean up orphaned schema entries (blocks that were removed)
  const validBlockKeys = collectAllBlockKeysFromPageBlocks(pageBlocks);
  //@ts-ignore
  const pageProperties = valuesSchema.properties[pageKey]?.properties || {};

  for (const schemaKey in pageProperties) {
    // skip layout schema
    if (schemaKey === layoutData.name) continue;

    // remove schema if block no longer exists
    if (!validBlockKeys.has(schemaKey)) {
      //@ts-ignore
      delete valuesSchema.properties[pageKey].properties[schemaKey];
    }
  }

  return {
    type: 'success',
    data: {
      valuesSchema,
      htmlTemplate: {
        pages: updatedHtmlTemplatePages,
      },
      pageBlock: {
        html: pageBlockHtml,
        name: pageKey,
        display_name: pageTemplateName,
      },
    },
  };
};

const extractSectionsFromTemplate = (
  blocks: { ID: number; html: string }[],
): {
  Slug: string;
  DisplayName: string;
}[] => {
  const sectionRegex = /<section\s+[^>]*id=["']([^"']+)["'][^>]*data-display-name=["']([^"']+)["'][^>]*>/gi;
  const sections: { Slug: string; DisplayName: string }[] = [];

  (blocks || []).forEach((block) => {
    let match;
    while ((match = sectionRegex.exec(block.html)) !== null) {
      const [, Slug, DisplayName] = match;
      sections.push({ Slug, DisplayName });
    }
  });

  return sections;
};

const replaceAliasesInContextData = (pageBlocks: any[], contextData: any, templateData: any): any => {
  if (!pageBlocks || !contextData || !templateData?.ValuesSchema) {
    return contextData;
  }

  const aliasMap = new Map<string, string>();

  const findGqlQueryKeys = (obj: any, parentKey: string = '') => {
    if (!obj || typeof obj !== 'object') return;

    for (const [key, value] of Object.entries(obj)) {
      if (key === 'inputOptions' && value && typeof value === 'object') {
        const inputOptions = value as any;

        if (inputOptions.gqlQueryKey && typeof inputOptions.gqlQueryKey === 'string') {
          aliasMap.set(inputOptions.gqlQueryKey, parentKey);
        }
      } else if (value && typeof value === 'object') {
        findGqlQueryKeys(value, key);
      }
    }
  };

  findGqlQueryKeys(templateData.ValuesSchema);

  if (aliasMap.size === 0) {
    return contextData;
  }

  let processedContextData = { ...contextData };

  for (const [alias, originalKey] of aliasMap.entries()) {
    if (processedContextData[alias] !== undefined && originalKey) {
      processedContextData[originalKey] = processedContextData[alias];
      delete processedContextData[alias];
    }
  }

  // Backwards compatibility: migrate old block data to new unique keys
  processedContextData = migrateBlockDataToUniqueKeys(pageBlocks, processedContextData);

  return processedContextData;
};

// Helper to migrate old block-name-based data to new unique-key-based data
const migrateBlockDataToUniqueKeys = (pageBlocks: any[], contextData: any): any => {
  if (!pageBlocks || !contextData) {
    return contextData;
  }

  const migratedData = { ...contextData };

  // Create mapping of old block names to new unique keys
  const getAllBlocks = (blocks: any[]): any[] => {
    const allBlocks: any[] = [];

    for (const block of blocks) {
      allBlocks.push(block);

      // Add blocks from columns
      if (block.columns) {
        for (const columnBlocks of Object.values(block.columns)) {
          allBlocks.push(...(columnBlocks as any[]));
        }
      }
    }

    return allBlocks;
  };

  const allBlocks = getAllBlocks(pageBlocks);

  for (const block of allBlocks) {
    if (block.instanceId && block.name) {
      const oldKey = block.name;
      const newKey = getBlockKey(block.name, block.instanceId);

      // If data exists under old key but not new key, migrate it
      if (migratedData[oldKey] && !migratedData[newKey]) {
        migratedData[newKey] = migratedData[oldKey];
        // Don't delete old key yet - keep for backwards compatibility
      }
    }
  }

  return migratedData;
};

// helper function to find a block by instanceId in pageBlocks or nested columns
const findBlockByInstanceId = (
  pageBlocks: any[],
  instanceId: string,
): { block: any; isInColumn: boolean; columnPath?: string } | null => {
  if (!pageBlocks || !Array.isArray(pageBlocks)) {
    return null;
  }

  // check top-level blocks
  const topLevelBlock = pageBlocks.find((b: any) => b.instanceId === instanceId);
  if (topLevelBlock) {
    return { block: topLevelBlock, isInColumn: false };
  }

  // check blocks inside columns
  for (const pageBlock of pageBlocks) {
    if (pageBlock.columns) {
      for (const [columnName, columnBlocks] of Object.entries(pageBlock.columns)) {
        const foundBlock = (columnBlocks as any[]).find((cb: any) => cb.instanceId === instanceId);
        if (foundBlock) {
          return {
            block: foundBlock,
            isInColumn: true,
            columnPath: `${pageBlock.instanceId}.${columnName}`,
          };
        }
      }
    }
  }

  return null;
};

export const PageTemplateEditor: FC<{ templateData: DocumentTemplate; pageBlockId?: any }> = ({
  templateData,
  pageBlockId,
}) => {
  const allPages = templateData?.Template?.pages || [];
  const pageBlock = allPages.find((page) => page.id === pageBlockId);

  const { projectId } = useParams();
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(true);
  const [contextData, setContextData] = useState<any>(null);
  const [configuringBlockId, setConfiguringBlockId] = useState<string | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [blockSearchQuery, setBlockSearchQuery] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: layouts, isLoading: isLayoutsLoading } = useGetLayoutsQuery();
  const { data: blocks, isLoading: isBlocksLoading } = useGetBlocksQuery();
  const { data: queriesList, isLoading: isQueriesListLoading } = useGetProjectTemplateQueriesQuery(
    {
      projectTemplateId: +projectId!,
    },
    { skip: !projectId },
  );

  const orderedLayouts = [...(layouts ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const orderedBlocks = [...(blocks ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  // Filter blocks based on search query
  const filteredBlocks = orderedBlocks.filter((block) => {
    if (!blockSearchQuery.trim()) return true;
    const query = blockSearchQuery.toLowerCase();
    return block.displayName?.toLowerCase().includes(query) || block.name?.toLowerCase().includes(query);
  });

  const formFields = useDeepCompareMemo(() => {
    return [
      {
        name: 'display_name',
        inputType: 'text',
        type: 'string',
        label: 'Display Name',
      },
      {
        name: 'layout',
        inputType: 'select',
        type: 'string',
        label: 'Layout',
        inputOptions: {
          selectOptions: orderedLayouts.map((layout) => ({
            value: layout?.name,
            label: layout.displayName,
            data: layout,
          })),
        },
      },
      {
        name: 'query',
        inputType: 'select',
        type: 'string',
        label: 'Query',
        inputOptions: {
          selectOptions: (queriesList || []).map((query) => ({
            value: query?.ID,
            label: query?.DisplayName,
          })),
        },
      },
    ];
  }, [orderedLayouts, queriesList]);

  const [updateBlock, { isLoading: isUpdateBlockLoading }] = useUpdateMicrositeTemplateBlockMutation();
  const [updateDocumentTemplate, { isLoading: isUpdateDocumentTemplateLoading }] = useUpdateDocumentTemplateMutation();

  const isLoading =
    isUpdateBlockLoading ||
    isUpdateDocumentTemplateLoading ||
    isFetching ||
    isQueriesListLoading ||
    isBlocksLoading ||
    isLayoutsLoading;

  const customFormMethods = useForm({
    mode: 'onChange',
    resolver: ajvResolver(formSchema as any, {
      formats: fullFormats,
    }),
  });

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { isDirty },
  } = customFormMethods;

  const selectedLayout: { data: Block; value: string; label: string } | null = watch('layout') ?? null;
  const pageBlocks = watch('pageBlocks');
  const selectedQuery = watch('query');

  const contextDataWithSections = useDeepCompareMemo(() => {
    const sections = extractSectionsFromTemplate(pageBlocks);
    let processedContextData = { ...(contextData || {}), Sections: sections };

    if (pageBlocks && pageBlocks.length > 0 && contextData) {
      processedContextData = replaceAliasesInContextData(pageBlocks, processedContextData, templateData);
    }

    return processedContextData;
  }, [pageBlocks, contextData, templateData]);

  const selectedQueryData = useDeepCompareMemo(
    () => (queriesList || []).find((item) => item?.ID === selectedQuery?.value),
    [selectedQuery, queriesList],
  );

  // Function to save template structure and clean up schemas
  const saveTemplateStructure = useCallback(
    async (blocksToSave: any[]) => {
      if (!pageBlock) return;

      try {
        const serializedPageBlocks = serializePageBlocksForStorage(blocksToSave);

        // Clean up orphaned schema entries
        const validBlockKeys = collectAllBlockKeysFromPageBlocks(blocksToSave);
        const currentValuesSchema = { ...templateData.ValuesSchema };

        // @ts-expect-error
        if (currentValuesSchema.properties[pageBlock.name]?.properties) {
          // @ts-expect-error
          for (const schemaKey in currentValuesSchema.properties[pageBlock.name].properties) {
            // Skip layout schema
            if (schemaKey === watch('layout')?.data?.name) continue;

            // Remove schema if block no longer exists
            if (!validBlockKeys.has(schemaKey)) {
              // @ts-expect-error
              delete currentValuesSchema.properties[pageBlock.name].properties[schemaKey];
            }
          }
        }

        // Update blocks in the template (keep layout as first element)
        const updatedPages = templateData?.Template?.pages?.map((page: any) => {
          if (page.id === pageBlock.id) {
            return {
              ...page,
              blocks: [
                page.blocks[0], // Keep layout
                ...serializedPageBlocks,
              ],
            };
          }
          return page;
        });

        // Update both template and valuesSchema
        await updateDocumentTemplate({
          projectTemplateId: +projectId!,
          documentTemplateId: templateData.ID,
          data: {
            template: {
              ...templateData.Template,
              pages: updatedPages,
            },
            valuesSchema: currentValuesSchema,
          },
        }).unwrap();
      } catch (error) {
        console.error('Error saving template structure:', error);
      }
    },
    [pageBlock, templateData, projectId, updateDocumentTemplate, watch],
  );

  const handleAddBlock = async (block: Block) => {
    const instanceId = uuidv4().slice(0, 5);
    const newBlock = { instanceId: instanceId, ...block };

    let updatedBlocks;
    if (selectedColumn) {
      const currentPageBlocks = pageBlocks || [];
      const [sectionInstanceId, columnName] = selectedColumn.split('.');

      updatedBlocks = currentPageBlocks.map((b: any) => {
        if (b.instanceId === sectionInstanceId) {
          const columns = b.columns || {};
          const columnBlocks = columns[columnName] || [];

          return {
            ...b,
            columns: {
              ...columns,
              [columnName]: [...columnBlocks, newBlock],
            },
          };
        }
        return b;
      });

      setValue('pageBlocks', updatedBlocks, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    } else {
      updatedBlocks = [...(pageBlocks || []), newBlock];
      setValue('pageBlocks', updatedBlocks, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  };

  // Helper to clean up orphaned schema entries for removed blocks
  const cleanupBlockSchemas = useCallback(
    (currentPageBlocks: any[]) => {
      if (!pageBlock) return;

      const validBlockKeys = collectAllBlockKeysFromPageBlocks(currentPageBlocks);
      const currentValuesSchema = cloneDeep(templateData.ValuesSchema);

      // @ts-expect-error
      if (currentValuesSchema.properties[pageBlock.name]?.properties) {
        // @ts-expect-error
        const pageProperties = { ...currentValuesSchema.properties[pageBlock.name].properties };

        for (const schemaKey in pageProperties) {
          // Skip layout schema
          if (schemaKey === watch('layout')?.data?.name) continue;

          // Remove schema if block no longer exists
          if (!validBlockKeys.has(schemaKey)) {
            delete pageProperties[schemaKey];
          }
        }

        // @ts-expect-error
        currentValuesSchema.properties[pageBlock.name].properties = pageProperties;

        // Update the templateData
        updateDocumentTemplate({
          projectTemplateId: +projectId!,
          documentTemplateId: templateData.ID,
          data: { valuesSchema: currentValuesSchema },
        });
      }
    },
    [pageBlock, templateData, watch, updateDocumentTemplate, projectId],
  );

  const updateBlockValuesSchema = useCallback(
    async (block: any, schema: any, displayName?: string) => {
      if (!block || !pageBlock) return;

      const blockKey = getBlockKey(block.name, block.instanceId);

      // Check if legacy block exists (without instanceId)
      //@ts-ignore
      const pageBlockSchema = templateData.ValuesSchema.properties[pageBlock.name] as any;
      const legacyBlockExists = !block.instanceId && pageBlockSchema?.properties?.[block.name];

      // Determine which key to use for updating (preserve legacy key if it exists)
      const keyToUpdate = legacyBlockExists ? block.name : blockKey;

      // Update schema with displayName if provided
      const updatedSchema = { ...schema };
      if (displayName !== undefined) {
        updatedSchema.displayName = displayName || block.name;
      }

      const valuesSchema = {
        ...templateData.ValuesSchema,
        properties: {
          ...templateData.ValuesSchema.properties,
          [pageBlock.name]: {
            ...templateData.ValuesSchema.properties[pageBlock.name],
            properties: {
              // @ts-expect-error
              ...templateData.ValuesSchema.properties[pageBlock.name]?.properties,
              [keyToUpdate]: updatedSchema,
            },
          },
        },
      };

      // Update the block's displayName if provided
      if (displayName !== undefined) {
        // Update the block in pageBlocks via the form
        const currentBlocks = watch('pageBlocks') || [];
        const updateBlockDisplayName = (blocks: any[]): any[] => {
          return blocks.map((b: any) => {
            if (b.instanceId === block.instanceId) {
              return { ...b, displayName };
            }
            // Also check within columns
            if (b.columns) {
              const updatedColumns: any = {};
              for (const [columnName, columnBlocks] of Object.entries(b.columns)) {
                updatedColumns[columnName] = (columnBlocks as any[]).map((cb: any) =>
                  cb.instanceId === block.instanceId ? { ...cb, displayName } : cb,
                );
              }
              return { ...b, columns: updatedColumns };
            }
            return b;
          });
        };

        const updatedBlocks = updateBlockDisplayName(currentBlocks);
        setValue('pageBlocks', updatedBlocks);
      }

      try {
        await updateDocumentTemplate({
          projectTemplateId: +projectId!,
          documentTemplateId: templateData.ID,
          data: { valuesSchema },
        }).unwrap();
      } catch (error: any) {
        const message = `Error updating document Template Schema`;
        dispatch(
          addNotification({
            type: 'error',
            message: message,
          }),
        );
        console.error(message, error);
      }
    },
    [templateData, pageBlock, projectId],
  );

  const handleDynamicFormAction = async (data: any) => {
    if (!pageBlock) {
      return;
    }

    if (data === 'cancel') {
      return;
    }

    const layoutData: Block = data?.layout?.data;

    if (!layoutData || !layoutData?.name) {
      console.error('Error: no layout data!');
      return;
    }

    const generateTemplateResult = generateHtmlTemplate({
      layoutData: layoutData,
      valuesSchema: JSON.parse(JSON.stringify(templateData.ValuesSchema)),
      pages: templateData?.Template?.pages,
      pageBlocks: pageBlocks,
      allBlocks: blocks,
      pageTemplateName: data.display_name,
      pageBlock: pageBlock,
      sections: contextDataWithSections?.Sections || [],
    });

    if (generateTemplateResult?.type === 'error' && generateTemplateResult?.message) {
      dispatch(
        addNotification({
          type: 'error',
          message: generateTemplateResult.message,
        }),
      );
      console.error(generateTemplateResult.message);
      return;
    }

    if (!generateTemplateResult?.data) {
      const message = 'No generated template data!';
      dispatch(
        addNotification({
          type: 'error',
          message: message,
        }),
      );
      console.error(message);
      return;
    }

    try {
      let documentTemplateData: {
        template: HtmlTemplate;
        valuesSchema: any;
        query_id?: number;
      } = {
        template: generateTemplateResult.data.htmlTemplate,
        valuesSchema: generateTemplateResult.data.valuesSchema,
      };

      documentTemplateData['query_id'] = selectedQuery.value;

      await updateDocumentTemplate({
        projectTemplateId: +projectId!,
        documentTemplateId: templateData.ID,
        data: documentTemplateData,
      }).unwrap();

      await updateBlock({
        blockId: pageBlock.id!,
        data: generateTemplateResult.data.pageBlock,
      });
    } catch (error: any) {
      const msg = 'Error updating page template';
      dispatch(
        addNotification({
          type: 'error',
          message: msg,
        }),
      );
    }
  };

  useDeepCompareEffect(() => {
    const getInitialValues = async () => {
      if (!pageBlock) {
        return;
      }

      if (!isFetching) {
        setIsFetching(true);
      }

      let initialValuesToSet: any = {
        display_name: pageBlock?.displayName || '',
        layout: null,
        query: templateData?.Query
          ? {
              value: templateData?.Query?.ID,
              label: templateData?.Query?.DisplayName,
            }
          : null,
      };

      const _layoutFromPageTemplate = (pageBlock.blocks || []).find(
        // @ts-expect-error
        (block: Pick<Block, 'type'>) => block.type === 'layout',
      );

      const _layoutBlock = (orderedLayouts ?? []).find(
        // @ts-expect-error
        (layoutBlock) => layoutBlock?.name === _layoutFromPageTemplate?.name,
      );

      if (!_layoutBlock || !_layoutBlock?.name) {
        // const message = 'No layout data! Skipping initial values';
        // console.warn(message);
        reset(initialValuesToSet);
        setIsFetching(false);
        return;
      }

      try {
        // check if we have saved blocks with column information
        const savedBlocks = pageBlock?.blocks;

        let mappedBlocks;

        if (savedBlocks && Array.isArray(savedBlocks)) {
          // restore from saved data with column information (skip layout which is first element)
          mappedBlocks = await Promise.all(
            savedBlocks
              .slice(1) // Skip layout (first element) since it's managed separately
              .map(async (block: any) => {
                let blockData = null;

                if (block?.id) {
                  try {
                    const response = await getAxiosApiV2WithAuthorization({
                      url: `${config.assetServiceBaseUrl}/blocks/${block.id}`,
                    });
                    blockData = response?.data;
                  } catch (error: any) {
                    console.error(`Error fetching block data for block ${block.name}!`, error);
                    return null;
                  }
                } else {
                  blockData = workspaceBlocksMockByTemplateNameMock[_layoutBlock.name]?.find(
                    (item) => item.Name === block.name,
                  );
                }
                if (!blockData) {
                  console.error(`Data not found for block: ${block.name}`);
                  return null;
                }

                const restoredBlock = {
                  ...mapBlockToStore(blockData),
                  instanceId: block.instanceId || uuidv4().slice(0, 5),
                  displayName: block.displayName,
                };

                // restore columns if they exist
                if (block.columns) {
                  const restoredColumns: any = {};

                  for (const [columnName, columnBlocks] of Object.entries(block.columns)) {
                    const restoredColumnBlocks = await Promise.all(
                      (columnBlocks as any[]).map(async (cb: any) => {
                        let columnBlockData = null;

                        if (cb?.id) {
                          try {
                            const response = await getAxiosApiV2WithAuthorization({
                              url: `${config.assetServiceBaseUrl}/blocks/${cb.id}`,
                            });
                            columnBlockData = response?.data;
                          } catch (error: any) {
                            console.error(`Error fetching column block data for ${cb.name}!`, error);
                            return null;
                          }
                        }

                        if (!columnBlockData) {
                          console.error(`Data not found for column block: ${cb.name}`);
                          return null;
                        }

                        return {
                          ...mapBlockToStore(columnBlockData),
                          instanceId: cb.instanceId || uuidv4().slice(0, 5),
                          displayName: cb.displayName,
                        };
                      }),
                    );

                    restoredColumns[columnName] = restoredColumnBlocks.filter(Boolean);
                  }

                  //@ts-ignore
                  restoredBlock.columns = restoredColumns;
                }

                return restoredBlock;
              }),
          );
        } else {
          // fallback to old method (no column data) - preserve existing instanceIds from form
          const currentPageBlocks = watch('pageBlocks') || [];

          mappedBlocks = await Promise.all(
            (pageBlock?.blocks || [])
              // @ts-expect-error
              .filter((block: Pick<Block, 'id' | 'type' | 'name'>) => block.type !== 'layout')
              // @ts-expect-error
              .map(async (block: Pick<Block, 'id' | 'type' | 'name'>) => {
                let blockData = null;

                if (block?.id) {
                  try {
                    const response = await getAxiosApiV2WithAuthorization({
                      url: `${config.assetServiceBaseUrl}/blocks/${block.id}`,
                    });
                    blockData = response?.data;
                  } catch (error: any) {
                    console.error(`Error fetching block data for block ${block.name}!`, error);
                    return null;
                  }
                } else {
                  blockData = workspaceBlocksMockByTemplateNameMock[_layoutBlock.name]?.find(
                    (item) => item.Name === block.name,
                  );
                }

                if (!blockData) {
                  console.error(`Data not found for block: ${block.name}`);
                  return null;
                }

                // Try to preserve existing instanceId from current form state
                const existingBlock = currentPageBlocks.find((pb: any) => pb.name === block.name && pb.id === block.id);
                const instanceId = existingBlock?.instanceId || uuidv4().slice(0, 5);

                return {
                  ...mapBlockToStore(blockData),
                  instanceId,
                };
              }),
          );
        }

        const filteredBlocks = mappedBlocks.filter(Boolean);

        initialValuesToSet['layout'] = _layoutBlock
          ? {
              value: _layoutBlock.name,
              label: _layoutBlock.displayName,
              data: _layoutBlock,
            }
          : null;
        initialValuesToSet['pageBlocks'] = filteredBlocks;

        reset(initialValuesToSet);
      } catch (error) {
        const message = `Error getting initial values!`;
        dispatch(
          addNotification({
            type: 'error',
            message: message,
          }),
        );
        console.error(message, error);
      } finally {
        setIsFetching(false);
      }
    };

    getInitialValues();
  }, [pageBlock, orderedLayouts]);

  useDeepCompareEffect(() => {
    const fetchData = async () => {
      if (!selectedQueryData) {
        return;
      }

      const fetchUrl = `${config.assetServiceBaseUrl}/v2/project-templates/${projectId}/graphql`;

      try {
        //@todo: use rtk instead
        const response = await getAxiosApiV2WithAuthorization({
          method: 'POST',
          url: fetchUrl,
          data: { query: selectedQueryData.Query },
        });

        setContextData(response.data?.custom?.data ? response.data.custom.data : response.data);
      } catch (error: any) {
        const message = 'Error fetching context data!';
        dispatch(
          addNotification({
            type: 'error',
            message: message,
          }),
        );
        console.error(message);
      }
    };

    fetchData();

    return ProjectTemplateWorkbookUploadEventBus.subscribe(
      '@clatter/PROJECT_TEMPLATE_WORKBOOK_UPLOAD_COMPLETED',
      fetchData,
    );
  }, [projectId, selectedQueryData, dispatch]);

  return (
    <StyledPageTemplateEditor key={pageBlock?.id}>
      <Grid container size={12} sx={{ gap: 4 }}>
        {!isExpanded && (
          <Grid size={{ xs: 12, md: 3 }}>
            <FolderManagerDynamicForm
              formSchema={{ fields: formFields, validation: formSchema }}
              customFormMethods={customFormMethods}
              onAction={handleDynamicFormAction}
              buttonsTargetContainerId="header-form-buttons"
              isLoading={isLoading}
              minWidth="100%"
            >
              <NgDivider minHeight={10} spacing="small" />

              <div>
                <label>Blocks</label>
                <input
                  type="text"
                  placeholder="Search blocks..."
                  value={blockSearchQuery}
                  onChange={(e) => setBlockSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '8px',
                    border: '1px solid lightgray',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
                <div className="blocks-list-container">
                  <ul>
                    {filteredBlocks.map((block) => (
                      <li key={block.id} className={classNames({ disabled: selectedLayout === null })}>
                        <BlockThumbnailPreview block={block} allBlocks={blocks || []} />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: '8px',
                          }}
                        >
                          <span>{block.displayName}</span>
                          <StyledIconButton onClick={() => handleAddBlock(block)}>
                            <AddIcon />
                          </StyledIconButton>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {filteredBlocks.length === 0 && blockSearchQuery && (
                  <NgErrorMessage message={`No blocks found matching "${blockSearchQuery}"`} />
                )}

                {!selectedLayout && (
                  <NgErrorMessage message="Please select a layout in order to add blocks to the template." />
                )}
              </div>
            </FolderManagerDynamicForm>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: isExpanded ? 12 : 8 }}>
          <Controller
            name="pageBlocks"
            render={({ field }) => {
              const handleAction = (action: NgAction<any>) => {
                if (action.type === 'EXPAND') {
                  setIsExpanded(true);
                  return;
                }

                if (action.type === 'COLLAPSE') {
                  setIsExpanded(false);
                  return;
                }

                if (action.type === 'SELECT_COLUMN') {
                  if (selectedColumn === action.payload) {
                    setSelectedColumn(null);
                    return;
                  }

                  setSelectedColumn(action.payload);
                  return;
                }

                if (action.type === 'MOVE_UP_IN_COLUMN' || action.type === 'MOVE_DOWN_IN_COLUMN') {
                  const { block: _block, columnPath, index } = action.payload;
                  const [sectionInstanceId, columnName] = columnPath.split('.');

                  const updatedBlocks = field.value.map((b: any) => {
                    if (b.instanceId === sectionInstanceId) {
                      const columnBlocks = [...(b.columns?.[columnName] || [])];

                      if (action.type === 'MOVE_UP_IN_COLUMN' && index > 0) {
                        [columnBlocks[index - 1], columnBlocks[index]] = [columnBlocks[index], columnBlocks[index - 1]];
                      } else if (action.type === 'MOVE_DOWN_IN_COLUMN' && index < columnBlocks.length - 1) {
                        [columnBlocks[index + 1], columnBlocks[index]] = [columnBlocks[index], columnBlocks[index + 1]];
                      }

                      return {
                        ...b,
                        columns: {
                          ...b.columns,
                          [columnName]: columnBlocks,
                        },
                      };
                    }
                    return b;
                  });

                  field.onChange(updatedBlocks);

                  return;
                }

                if (action.type === 'REMOVE_FROM_COLUMN') {
                  const { block: _block, columnPath, index } = action.payload;
                  const [sectionInstanceId, columnName] = columnPath.split('.');

                  const updatedBlocks = field.value.map((b: any) => {
                    if (b.instanceId === sectionInstanceId) {
                      const columnBlocks = [...(b.columns?.[columnName] || [])];
                      columnBlocks.splice(index, 1);

                      return {
                        ...b,
                        columns: {
                          ...b.columns,
                          [columnName]: columnBlocks,
                        },
                      };
                    }
                    return b;
                  });

                  field.onChange(updatedBlocks);

                  // clean up schema for removed block
                  cleanupBlockSchemas(updatedBlocks);

                  return;
                }

                if (action.type === listActionTypes.OPEN_SETTINGS) {
                  if (isDirty) return;
                  setConfiguringBlockId(action.payload.instanceId ?? null);
                  return;
                }

                const updated = updateLayoutByAction({
                  items: field.value,
                  block: action.payload,
                  action: action.type,
                });

                field.onChange([...updated]);
              };

              return (
                <>
                  {configuringBlockId && (
                    <Dialog fullScreen open onClose={() => setConfiguringBlockId(null)} sx={{ zIndex: 1500 }}>
                      <AppBar sx={{ position: 'relative', background: 'white' }}>
                        <Toolbar>
                          <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setConfiguringBlockId(null)}
                            aria-label="close"
                          >
                            <CloseIcon style={{ color: 'black' }} />
                          </IconButton>
                          <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }}>
                            Close
                          </Typography>
                        </Toolbar>
                      </AppBar>

                      {(() => {
                        // Find block in top-level or within columns
                        const foundBlock = findBlockByInstanceId(field.value, configuringBlockId);

                        if (!foundBlock) {
                          return <div style={{ padding: '20px' }}>Block not found</div>;
                        }

                        return (
                          <BlockInputsSettings
                            entrypoint={foundBlock.block}
                            blocks={blocks ?? []}
                            schema={
                              //@ts-ignore
                              templateData.ValuesSchema.properties[pageBlock.name]?.properties[
                                getBlockKey(foundBlock.block.name, foundBlock.block.instanceId)
                              ] ||
                              //@ts-ignore
                              templateData.ValuesSchema.properties[pageBlock.name]?.properties[foundBlock.block.name]
                            }
                            onSave={(schema: any, displayName?: string) =>
                              updateBlockValuesSchema(foundBlock.block, schema, displayName)
                            }
                          />
                        );
                      })()}
                    </Dialog>
                  )}

                  <HtmlTemplatePreview
                    data={field}
                    allPageBlocks={allPages}
                    pageBlock={pageBlock}
                    blocks={blocks ?? []}
                    layout={selectedLayout?.data ?? null}
                    isLoading={isLoading}
                    isDirty={isDirty}
                    context={contextDataWithSections}
                    selectedColumn={selectedColumn}
                    isExpanded={isExpanded}
                    onAction={handleAction}
                    valuesSchema={templateData.ValuesSchema}
                  />
                </>
              );
            }}
            control={control}
          />
        </Grid>
      </Grid>

      <Grid container size={12}>
        <Grid size={12}>
          <div id="header-form-buttons"></div>
        </Grid>
      </Grid>
    </StyledPageTemplateEditor>
  );
};
